import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Plus, Trash2, Key, Download, Terminal, Monitor, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatDate, formatBytes } from '@/lib/utils';
import { CreateUserDialog } from './CreateUserDialog';

interface User {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user';
  isActive: boolean;
  diskQuota: number;
  diskUsed: number;
  createdAt: string;
}

export function UsersTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const data = await api.getUsers({
        search: searchTerm || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
      });
      setUsers(data as User[]);
    } catch (_error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    setIsLoading(true);
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id: string, username: string) => {
    if (
      !confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)
    ) {
      return;
    }

    try {
      await api.deleteUser(id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (_error) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (id: string, username: string) => {
    if (!confirm(`Reset password for "${username}"? A new temporary password will be generated.`)) {
      return;
    }

    try {
      const data = await api.resetUserPassword(id);

      // Show password in an alert with copy instruction
      const message = `New temporary password for ${username}:\n\n${data.tempPassword}\n\nPlease copy this password and share it with the user securely.`;
      alert(message);

      // Try to copy to clipboard
      try {
        await navigator.clipboard.writeText(data.tempPassword);
        toast.success('Password reset and copied to clipboard');
      } catch {
        toast.success('Password reset successfully - please copy from the dialog');
      }
    } catch (_error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDownloadConfig = async (
    userId: string,
    username: string,
    type: 'sharex' | 'bash' | 'powershell'
  ) => {
    try {
      if (type === 'sharex') {
        await api.downloadUserShareXConfig(userId, username);
      } else if (type === 'bash') {
        await api.downloadUserBashScript(userId);
      } else {
        await api.downloadUserPowerShellScript(userId);
      }

      toast.success(
        `${type === 'sharex' ? 'ShareX config' : type === 'bash' ? 'Bash script' : 'PowerShell script'} downloaded`
      );
    } catch (_error) {
      toast.error('Failed to download config');
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const activeFiltersCount = (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Users</CardTitle>
            <CardDescription>Manage user accounts and permissions</CardDescription>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create User
          </Button>
        </CardHeader>
        <CardContent>
          {/* Filter Bar */}
          <div className="mb-6 space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search users by username or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Role Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="min-w-[120px] justify-between">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      {roleFilter
                        ? roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)
                        : 'All Roles'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Filter by Role</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setRoleFilter('')}>All Roles</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRoleFilter('admin')}>Admin</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setRoleFilter('user')}>User</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Status Filter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="min-w-[120px] justify-between">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4" />
                      {statusFilter
                        ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
                        : 'All Status'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setStatusFilter('')}>
                    All Status
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('active')}>
                    Active
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('inactive')}>
                    Inactive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Active Filters Display */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {roleFilter && (
                  <Badge variant="secondary">
                    Role: {roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)}
                  </Badge>
                )}
                {statusFilter && (
                  <Badge variant="secondary">
                    Status: {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                  </Badge>
                )}
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 text-xs">
                  Clear All
                </Button>
              </div>
            )}
          </div>
          {users.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              {searchTerm || roleFilter || statusFilter
                ? 'No users match your filters'
                : 'No users found'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Storage</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.isActive ? 'success' : 'destructive'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatBytes(user.diskUsed)} / {formatBytes(user.diskQuota)}
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" title="Download Configs">
                              <Download className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Download Configs</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDownloadConfig(user.id, user.username, 'sharex')}
                            >
                              <Monitor className="mr-2 h-4 w-4" />
                              ShareX Config
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDownloadConfig(user.id, user.username, 'bash')}
                            >
                              <Terminal className="mr-2 h-4 w-4" />
                              Bash Script
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                handleDownloadConfig(user.id, user.username, 'powershell')
                              }
                            >
                              <Terminal className="mr-2 h-4 w-4" />
                              PowerShell Script
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetPassword(user.id, user.username)}
                          title="Reset Password"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(user.id, user.username)}
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateUserDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={fetchUsers}
      />
    </>
  );
}
